import { useCallback, useEffect, useRef, useState } from 'react'
import { useFeatureFlag } from '../lib/featureFlags'
import {
  fetchAllProducts,
  fetchDepartments,
  fetchProductCategories,
  fetchOperators,
  updateOperatorMandante,
  getProduct,
  createProduct,
  updateProduct,
  type Product,
  type Department,
  type ProductCategory,
  type Operator,
  type ProductCreateRequest,
} from '../api/catalog'
import { fetchMe } from '../api/auth'
import { api } from '../api/client'
import {
  activateCatalogEntity,
  cloneCatalogEntity,
  deactivateCatalogEntity,
  describeUsages,
} from '../api/catalogGovernance'
import {
  FormSection, Button, ButtonLink, Callout, Checkbox, DataTable, DataTableEmptyRow, Dialog, FilterBar, IconButton,
  LoadingState, Page, PageHeader, Select, StatusPill, TextField, useSnackbar, useConfirm,
  usePrompt,
} from '../components/ui'

/**
 * TASK-0077: fields the create/edit form actually collects, out of the 13 the backend accepts
 * (`ProductCreateRequest`/`ProductUpdateRequest` in `api/catalog.ts`). Chosen from real live
 * data (307 products) rather than guessing:
 *   - name/slug/code/basePrice/commissionValue/departmentId: task's explicit minimum, all
 *     populated on 100% of real products.
 *   - categoryId: 307/307 real products have it set.
 *   - operatorId: 302/307 (98%).
 *   - description: 302/307 (98%).
 *   - pricingType: always populated, but every single real product uses the same value
 *     ("commission_only") and there's no enum anywhere in the codebase for it (plain
 *     `varchar(50)` on the backend) — a free-text input, not a guessed `<select>`.
 *   - requiresApproval: 0/307 true today, but it's a single checkbox — negligible complexity
 *     to include even though currently unused in practice.
 * Deliberately omitted (see `defaultProductForm` below for the full rationale): `subcategoryId`
 * (0/307 used), `sddAmount`/`recurringMonthlyAmount` (both meaningless while every real product
 * is `commission_only`, and nothing in the codebase defines which `pricingType` values would
 * make them apply).
 *
 * TASK-0156 (REQ-006-007): `applicableCustomerType` added — `''` is this field's own "unset"
 * sentinel (same convention as `departmentId`/`categoryId`/`operatorId` above), submitted as
 * `null` (see `handleSave` below), which the backend reads as "applies to both person and
 * company", not "unset" — see `Product.ApplicableCustomerType`'s own doc comment (backend).
 *
 * TASK-0164 (REQ-043-002): `validFrom`/`validTo` added — same `''` "unset" sentinel convention,
 * holding the raw `YYYY-MM-DD` string an `<input type="date">` produces (see the form below),
 * submitted as `null` when empty (see `handleSave`). Not economic/sensitive data, same treatment
 * as `applicableCustomerType`.
 *
 * REQ-018-002: `companyRevenue` added — the operator price list's "Ricavo azienda". Same `''`
 * "unset" sentinel as `basePrice`/`commissionValue`, but submitted as `null` rather than blocking
 * the save, because the backend column is genuinely nullable AND `Product.Update` reads a null as
 * "no change": that is what lets a caller WITHOUT CanSeeRealAmounts (whose prefill has the key
 * absent — see `openEdit`) still edit a product's other fields without silently wiping a company
 * revenue they were never shown.
 */
interface ProductFormState {
  name: string
  slug: string
  code: string
  basePrice: number | ''
  commissionValue: number | ''
  companyRevenue: number | ''
  departmentId: number | ''
  categoryId: number | ''
  operatorId: number | ''
  pricingType: string
  description: string
  requiresApproval: boolean
  applicableCustomerType: string
  validFrom: string
  validTo: string
  mandanteId: number | ''
  visibleToAgents: boolean
  isOption: boolean
}

const defaultProductForm: ProductFormState = {
  name: '',
  slug: '',
  code: '',
  basePrice: '',
  commissionValue: '',
  companyRevenue: '',
  departmentId: '',
  categoryId: '',
  operatorId: '',
  pricingType: '',
  description: '',
  requiresApproval: false,
  applicableCustomerType: '',
  validFrom: '',
  validTo: '',
  mandanteId: '',
  visibleToAgents: true,
  isOption: false,
}

/** Simple ASCII slugifier for the "Nome" -> "Slug" auto-fill convenience below — strips accents
 * and anything that isn't a-z/0-9 into single dashes. Only ever runs client-side as a typing
 * convenience; the slug field itself stays a plain editable text input. */
function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function ProductsPage() {
  const configOn = useFeatureFlag('pratiche.campi_condizionali')
  const { show } = useSnackbar()
  const confirm = useConfirm()
  const prompt = usePrompt()
  const [products, setProducts] = useState<Product[]>([])
  const [departments, setDepartments] = useState<Department[]>([])
  const [categories, setCategories] = useState<ProductCategory[]>([])
  const [operators, setOperators] = useState<Operator[]>([])
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [selectedDept, setSelectedDept] = useState('')
  // TASK-0156 (REQ-006-007): both filter server-side, same reasoning as selectedDept just above —
  // a single operator's/customer-type's product count can exceed one page, so filtering only an
  // already-loaded page would silently under-report results.
  const [selectedOperator, setSelectedOperator] = useState('')
  const [selectedCustomerType, setSelectedCustomerType] = useState('')
  // Coraly A: '' = tutti, 'hidden' = solo i prodotti nascosti agli agenti.
  const [visibilityFilter, setVisibilityFilter] = useState('')
  const [mandanti, setMandanti] = useState<{ id: number; name: string }[]>([])
  const [showOperators, setShowOperators] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Guards against out-of-order responses: if the department filter changes again before an
  // in-flight fetchAllProducts() call resolves (itself possibly several fetchProducts() pages
  // deep), a slower earlier response must not overwrite the faster, more recent one once it
  // lands. Bumped on every effect run; a response only commits its data if it's still the latest
  // request when it resolves.
  const requestSeqRef = useRef(0)

  // TASK-0077: create/edit modal state.
  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [form, setForm] = useState<ProductFormState>(defaultProductForm)
  // Tracks whether the user has directly touched the Slug field — while false, editing Nome
  // auto-fills Slug via slugify() (create-mode convenience only, see openEdit()/openCreate()).
  const [slugTouched, setSlugTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  // F-067: clona/attiva/"dove e' usato" sono riservati agli Admin (il gate reale e' sul server).
  const [isAdmin, setIsAdmin] = useState(false)
  useEffect(() => {
    void fetchMe()
      .then(me => setIsAdmin(me?.role === 'Administrator' || me?.role === 'EqualAdmin'))
      .catch(() => setIsAdmin(false))
  }, [])

  useEffect(() => {
    fetchDepartments()
      .then(depts => setDepartments(depts))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Errore nel caricamento'))
  }, [])

  // TASK-0077: categories/operators for the form's selectors, fetched once — small, mostly
  // static reference data (6 categories, 16 operators live today), same fetch-once pattern as
  // the departments effect above and as TeamPage's users/categories effect.
  useEffect(() => {
    api<{ items: { id: number; name: string }[] }>('/api/mandanti')
      .then(r => setMandanti(r.items)).catch(() => setMandanti([]))
  }, [])

  useEffect(() => {
    fetchProductCategories().then(res => setCategories(res.items)).catch(() => setCategories([]))
    fetchOperators().then(res => setOperators(res.items)).catch(() => setOperators([]))
  }, [])

  // Reparto is filtered server-side (re-fetching on change) rather than client-side: the backend
  // already supports `departmentId` as a real query param, and unlike `search`, a single
  // department's product count can exceed a single page (e.g. "Telefonia" alone has 230 real
  // products today), so filtering only one already-loaded page would silently under-report
  // results for that department.
  //
  // TASK-0073: this used to be a single `fetchProducts({ pageSize: 200, ... })` call, which
  // silently truncated the catalog (307 products, more than 200) and truncated every department
  // filter wider than 200 too (Telefonia's 230) — see `fetchAllProducts` in `api/catalog.ts` for
  // the fix (pages until the server's own `total` is satisfied, same pattern TASK-0067 used for
  // the Kanban board's fixed pageSize).
  //
  // TASK-0077: extracted into a standalone `reload` callback (same sequencing guard as before,
  // untouched) so the create/edit/delete handlers below can trigger the exact same refetch
  // after a successful mutation, instead of duplicating this logic.
  const reload = useCallback(() => {
    setLoading(true)
    setError(null)
    const seq = ++requestSeqRef.current
    const departmentId = selectedDept ? Number(selectedDept) : undefined
    // TASK-0156 (REQ-006-007): operatorId/customerType compose with departmentId exactly like
    // fetchAllProducts already composes departmentId with search — all are plain `AND`ed
    // server-side filters (see ProductsController.List).
    const operatorId = selectedOperator ? Number(selectedOperator) : undefined
    const customerType = selectedCustomerType || undefined
    return fetchAllProducts({
      ...(departmentId !== undefined ? { departmentId } : {}),
      ...(operatorId !== undefined ? { operatorId } : {}),
      ...(customerType !== undefined ? { customerType } : {}),
      ...(visibilityFilter === 'hidden' ? { visibleToAgents: false } : {}),
    })
      .then(prodResult => {
        if (seq !== requestSeqRef.current) return // a newer request has since superseded this one
        setProducts(prodResult.items)
        setTotal(prodResult.total)
      })
      .catch((e: unknown) => {
        if (seq !== requestSeqRef.current) return
        setError(e instanceof Error ? e.message : 'Errore nel caricamento')
      })
      .finally(() => {
        if (seq === requestSeqRef.current) setLoading(false)
      })
  }, [selectedDept, selectedOperator, selectedCustomerType, visibilityFilter])

  useEffect(() => { void reload() }, [reload])

  const filteredProducts = search
    ? products.filter(p => {
        const q = search.toLowerCase()
        return p.name.toLowerCase().includes(q) || (p.code ?? '').toLowerCase().includes(q)
      })
    : products

  function openCreate() {
    setForm(defaultProductForm)
    setEditId(null)
    setSlugTouched(false)
    setSaveError(null)
    setShowModal(true)
  }

  // TASK-0077: prefills from `GET /api/products/{id}` (`getProduct`), NOT from the row already
  // in `filteredProducts` — the list projection omits `description`/`pricingType`/
  // `requiresApproval` entirely (see `ProductDetail`'s doc comment in api/catalog.ts), so
  // building the form from the row would show those fields as blank even when the product
  // already has real values, and would submit an edit that (harmlessly, thanks to the
  // backend's per-field guards, but still incorrectly) claims to be untouched.
  async function openEdit(id: number) {
    setEditId(id)
    setSaveError(null)
    setShowModal(true)
    setLoadingDetail(true)
    try {
      const { product } = await getProduct(id)
      setForm({
        name: product.name,
        slug: product.slug,
        code: product.code ?? '',
        basePrice: product.basePrice,
        // TASK-0116: `product.commissionValue` is an ABSENT key (not 0/null) when the caller
        // lacks CanSeeRealAmounts (TASK-0115) — e.g. a CanManageProducts-authorized staff member
        // who does NOT also have CanSeeRealAmounts (the two flags are independent) opening an
        // existing product to edit its non-commission fields. Falling back to the form's own
        // "unset" sentinel (`''`, same as `basePrice`'s pattern) rather than leaving it
        // `undefined` is load-bearing: it's what makes `handleSave()`'s existing
        // `form.commissionValue === ''` check below actually catch this case and block the save
        // — leaving it `undefined` would silently skip that check (`undefined !== ''`) and let
        // `Number(undefined)` → `NaN` → `null` reach the save payload instead.
        commissionValue: product.commissionValue ?? '',
        // REQ-018-002: `''` covers BOTH cases — the key absent because the caller lacks
        // CanSeeRealAmounts (redacted, same as commissionValue above) and a genuinely null column
        // on a product with no company revenue recorded. They do not need distinguishing here:
        // `handleSave` submits `''` as `null`, which `Product.Update` reads as "no change", so a
        // redacted prefill can never overwrite a value the user was not shown.
        companyRevenue: product.companyRevenue ?? '',
        departmentId: product.departmentId ?? '',
        categoryId: product.categoryId ?? '',
        operatorId: product.operatorId ?? '',
        pricingType: product.pricingType ?? '',
        description: product.description ?? '',
        requiresApproval: product.requiresApproval,
        // TASK-0156: `''` is this field's own "unset" sentinel (same convention as
        // departmentId/categoryId/operatorId above) — `product.applicableCustomerType` is
        // genuinely `null`/`undefined` for a product that "applies to both". Fixed by the same
        // task's review pass: GET /api/products/{id}'s redacted branch was initially missing this
        // field from its allow-list, so it now correctly round-trips for every caller.
        applicableCustomerType: product.applicableCustomerType ?? '',
        // TASK-0164: same "unset" sentinel convention — product.validFrom/validTo are genuinely
        // null/undefined for a product with no validity restriction on that end.
        validFrom: product.validFrom ?? '',
        validTo: product.validTo ?? '',
        mandanteId: product.mandanteId ?? '',
        visibleToAgents: product.visibleToAgents ?? true,
        isOption: product.isOption ?? false,
      })
      // An existing product already has a real slug — editing its Nome must never silently
      // rewrite it out from under the user.
      setSlugTouched(true)
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'Errore nel caricamento del prodotto')
    } finally {
      setLoadingDetail(false)
    }
  }

  function closeModal() {
    setShowModal(false)
    setEditId(null)
    setForm(defaultProductForm)
    setSaveError(null)
  }

  function handleNameChange(name: string) {
    setForm(f => ({ ...f, name, slug: slugTouched ? f.slug : slugify(name) }))
  }

  function handleSlugChange(slug: string) {
    setSlugTouched(true)
    setForm(f => ({ ...f, slug }))
  }

  async function handleSave() {
    // TASK-0116: `form.commissionValue === ''` here is also what catches a redacted prefill from
    // `openEdit()` (see the comment there) — a caller without CanSeeRealAmounts editing an
    // existing product now gets `''` instead of a silently-missing value, so this pre-existing
    // check blocks the save with a visible error rather than letting `handleSave()` reach
    // `Number(form.commissionValue)` (`NaN`) below with a missing value.
    if (!form.name.trim() || !form.slug.trim() || form.basePrice === '' || form.commissionValue === '') {
      setSaveError('Nome, slug, prezzo base e commissione sono obbligatori')
      return
    }
    setSaving(true)
    setSaveError(null)
    try {
      // TASK-0077: builds a fully-shaped `ProductCreateRequest` even when editing — it's also a
      // valid `ProductUpdateRequest` (every field there is optional), and `Product.Update`
      // guards each field individually server-side, so sending explicit current values here
      // (rather than only the ones the user actually touched) is safe: it never wipes anything,
      // it just re-confirms the value already shown in the form.
      const payload: ProductCreateRequest = {
        name: form.name.trim(),
        slug: form.slug.trim(),
        code: form.code.trim() ? form.code.trim() : null,
        basePrice: Number(form.basePrice),
        // TASK-0116: safe to coerce unconditionally — the `form.commissionValue === ''` guard
        // above already returned before this point for both a genuinely empty field and a
        // redacted-then-defaulted-to-'' prefill (see `openEdit()`), so `form.commissionValue` is
        // guaranteed to be a real `number` here, never `''`/`undefined` → never `NaN`.
        commissionValue: Number(form.commissionValue),
        // REQ-018-002: `''` (unset, or a redacted prefill — see `openEdit`) submits as `null`,
        // which `Product.Update` reads as "no change". Unlike `commissionValue` this is NOT
        // blocked by the required-fields guard above: the column is nullable by design, so an
        // empty "Ricavo azienda" is a legitimate product, not an incomplete form.
        companyRevenue: form.companyRevenue !== '' ? Number(form.companyRevenue) : null,
        departmentId: form.departmentId !== '' ? Number(form.departmentId) : null,
        categoryId: form.categoryId !== '' ? Number(form.categoryId) : null,
        operatorId: form.operatorId !== '' ? Number(form.operatorId) : null,
        pricingType: form.pricingType.trim() ? form.pricingType.trim() : null,
        description: form.description.trim() ? form.description.trim() : null,
        requiresApproval: form.requiresApproval,
        // TASK-0156: `''` (this field's "unset" sentinel, same convention as departmentId/
        // categoryId/operatorId above) submits as `null` — the backend reads a null
        // ApplicableCustomerType as "applies to both", not "unset", so this is the correct value
        // to send for "no restriction", not an omission to guard against.
        applicableCustomerType: form.applicableCustomerType !== '' ? form.applicableCustomerType : null,
        // TASK-0164: `''` (this field's "unset" sentinel) submits as `null` — no validity
        // restriction on that end, not an omission to guard against.
        validFrom: form.validFrom !== '' ? form.validFrom : null,
        validTo: form.validTo !== '' ? form.validTo : null,
        // Coraly A: '' = mandante dell'operatore; in modifica svuota quella del prodotto.
        mandanteId: form.mandanteId !== '' ? Number(form.mandanteId) : null,
        ...(editId !== null && form.mandanteId === '' ? { clearMandante: true } : {}),
        visibleToAgents: form.visibleToAgents,
        isOption: form.isOption,
      }
      if (editId !== null) {
        await updateProduct(editId, payload)
      } else {
        await createProduct(payload)
      }
      closeModal()
      void reload()
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'Errore durante il salvataggio')
    } finally {
      setSaving(false)
    }
  }

  // TASK-0077: `deleteProduct` calls the backend's `Delete`, which soft-deletes (deactivates,
  // doesn't remove the row — see `deleteProduct`'s doc comment in api/catalog.ts). The row is
  // removed from local state directly rather than via `reload()`: a refetch would (correctly)
  // still include it, now badged "Inattivo", since this table intentionally shows inactive
  // products too and TASK-0061/0073's filter/pagination logic is out of scope to change here.
  async function handleDelete(product: Product) {
    if (!(await confirm({ title: 'Eliminare il prodotto?', message: `Eliminare il prodotto "${product.name}"?`, confirmLabel: 'Elimina', destructive: true }))) return
    try {
      // F-067 (REQ-293-001): se il prodotto e' in uso il server risponde 409 con l'elenco degli usi e si
      // chiede una seconda conferma esplicita; solo allora la disattivazione parte con `confirm`.
      const first = await deactivateCatalogEntity('products', product.id, false)
      if (first.status === 'needs-confirm') {
        const ok = await confirm({
          title: 'Disattivare il prodotto?',
          message: (
            <span className="md-u-pre-line">
              {`Il prodotto "${product.name}" e' in uso:\n${describeUsages(first.usages)}\n\nDisattivarlo comunque?`}
            </span>
          ),
          confirmLabel: 'Disattiva',
          destructive: true,
        })
        if (!ok) return
        await deactivateCatalogEntity('products', product.id, true)
      }
      setProducts(prev => prev.filter(p => p.id !== product.id))
      setTotal(prev => Math.max(0, prev - 1))
    } catch (e: unknown) {
      show({ message: e instanceof Error ? e.message : "Errore durante l'eliminazione", tone: 'error' })
    }
  }

  // F-067: il clone nasce disattivo; lo slug gia' usato torna come errore 400 leggibile.
  async function handleClone(product: Product) {
    const name = await prompt({
      title: 'Clona il prodotto',
      label: 'Nome del nuovo prodotto',
      initialValue: `${product.name} (copia)`,
      required: true,
      confirmLabel: 'Continua',
    })
    if (!name) return
    const slug = await prompt({
      title: 'Clona il prodotto',
      label: 'Slug del nuovo prodotto',
      initialValue: `${product.slug}-copia`,
      required: true,
      confirmLabel: 'Clona',
    })
    if (!slug) return
    try {
      await cloneCatalogEntity('products', product.id, { name, slug })
      show({ message: 'Prodotto clonato: il clone e\' disattivo, va attivato dopo la verifica.', tone: 'success' })
      void reload()
    } catch (e: unknown) {
      show({ message: e instanceof Error ? e.message : 'Errore durante la clonazione', tone: 'error' })
    }
  }

  // F-067: si attiva solo se la verifica di configurazione passa; altrimenti si mostrano gli errori.
  async function handleActivate(product: Product) {
    try {
      const res = await activateCatalogEntity('products', product.id)
      if (!res.ok) {
        show({ message: `Il prodotto non si puo' attivare:\n${res.errors.map(e => `- ${e.message}`).join('\n')}`, tone: 'error' })
        return
      }
      void reload()
    } catch (e: unknown) {
      show({ message: e instanceof Error ? e.message : "Errore durante l'attivazione", tone: 'error' })
    }
  }

  // Top-level categories only (ParentId === null) — see ProductCategory's doc comment in
  // api/catalog.ts. Filtered to the form's currently selected department when one is set.
  const categoryOptions = categories.filter(
    c => c.parentId === null && (form.departmentId === '' || c.departmentId === form.departmentId),
  )
  const operatorOptions = operators.filter(
    o => form.departmentId === '' || o.departmentId === form.departmentId,
  )

  return (
    <Page>
      <PageHeader
        title="Prodotti"
        icon="inventory_2"
        count={total}
        actions={
          <>
            <Button type="button" variant="text" icon="business" onClick={() => setShowOperators(true)}>
              Mandanti operatori
            </Button>
            <Button type="button" variant="filled" icon="add" disabled={loadingDetail} onClick={openCreate}>
              Nuovo prodotto
            </Button>
          </>
        }
      />

      <FilterBar
        onReset={() => {
          setSearch('')
          setSelectedDept('')
          setSelectedOperator('')
          setSelectedCustomerType('')
          setVisibilityFilter('')
        }}
      >
        <Select
          label="Reparto"
          labelHidden
          value={selectedDept}
          onChange={e => setSelectedDept(e.target.value)}
        >
          <option value="">Reparto</option>
          {departments.map(d => (
            <option key={d.id} value={String(d.id)}>{d.name}</option>
          ))}
        </Select>
        <Select label="Categoria" labelHidden>
          <option value="">Categoria</option>
        </Select>
        {/* TASK-0156 (REQ-006-007): Product.OperatorId already existed as a real column and as a
            form field (see the dialog below), but had no list-level filter before this task — see
            ProductsController.List's new operatorId query param. */}
        <Select
          label="Operatore"
          labelHidden
          value={selectedOperator}
          onChange={e => setSelectedOperator(e.target.value)}
        >
          <option value="">Operatore</option>
          {operators.map(o => (
            <option key={o.id} value={String(o.id)}>{o.name}</option>
          ))}
        </Select>
        {/* TASK-0156: new customerType filter over Product.ApplicableCustomerType — see
            ProductsController.List's new customerType query param. A product with no
            restriction (ApplicableCustomerType === null) is included for either value. */}
        <Select
          label="Tipo cliente"
          labelHidden
          value={selectedCustomerType}
          onChange={e => setSelectedCustomerType(e.target.value)}
        >
          <option value="">Tipo cliente</option>
          <option value="person">Persona</option>
          <option value="company">Azienda</option>
        </Select>
        <Select
          label="Visibilità agenti"
          labelHidden
          value={visibilityFilter}
          onChange={e => setVisibilityFilter(e.target.value)}
        >
          <option value="">Visibilità agenti</option>
          <option value="hidden">Nascosti agli agenti</option>
        </Select>
        <TextField
          label="Cerca per nome, codice, operatore..."
          labelHidden
          placeholder="Cerca per nome, codice, operatore..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </FilterBar>

      {error && <Callout tone="error">{error}</Callout>}

      {/* Products table */}
      {loading ? (
        <LoadingState />
      ) : (
        <DataTable stickyHeader aria-label={`Catalogo: ${filteredProducts.length} prodotti`}>
          <thead>
            <tr>
              <th>Prodotto</th>
              <th>Codice</th>
              <th>Tipo prezzo</th>
              <th className="md-num">Commissione</th>
              {/* REQ-018-002: the operator price list's "Ricavo azienda". */}
              <th className="md-num">Ricavo azienda</th>
              <th>Stato</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.length === 0 ? (
              <DataTableEmptyRow colSpan={7} icon="inventory_2" title="Nessun prodotto trovato" />
            ) : filteredProducts.map(p => (
              <tr key={p.id}>
                <td>
                  <strong>{p.name}</strong>
                  {p.visibleToAgents === false && (
                    <> <StatusPill tone="warning">Nascosto agli agenti</StatusPill></>
                  )}
                  {p.isOption === true && (
                    <> <StatusPill tone="info">Opzione</StatusPill></>
                  )}
                </td>
                <td><small>{p.code ?? '—'}</small></td>
                <td>{p.pricingType ?? 'Una tantum'}</td>
                <td className="md-num">
                  {/* TASK-0116: `commissionValue` is an ABSENT key (not 0/null) for a caller
                      without CanSeeRealAmounts (TASK-0115) — showing 0 here would read as a
                      real (and misleadingly wrong) commission value, so a redacted row shows
                      an explicit "—" placeholder instead. */}
                  {p.commissionValue !== undefined ? (
                    <strong>€ {p.commissionValue.toLocaleString('it-IT')}</strong>
                  ) : (
                    <span>—</span>
                  )}
                </td>
                <td className="md-num">
                  {/* REQ-018-002: `companyRevenue` is an ABSENT key for a caller without
                      CanSeeRealAmounts, and legitimately `null` for a product with no company
                      revenue recorded. Both render as "—": showing 0 would read as a real
                      (and misleadingly wrong) figure, same reasoning as `commissionValue`. */}
                  {p.companyRevenue !== undefined && p.companyRevenue !== null ? (
                    <span>€ {p.companyRevenue.toLocaleString('it-IT')}</span>
                  ) : (
                    <span>—</span>
                  )}
                </td>
                <td>
                  {p.isActive
                    ? <StatusPill tone="success">Attivo</StatusPill>
                    : <StatusPill tone="neutral">Inattivo</StatusPill>
                  }
                </td>
                <td className="md-num">
                  <div className="md-cluster justify-content-end">
                    <IconButton
                      icon="edit"
                      size="sm"
                      aria-label="Modifica"
                      disabled={loadingDetail}
                      onClick={() => void openEdit(p.id)}
                    />
                    {isAdmin && (
                      <>
                        <IconButton
                          icon="content_copy"
                          size="sm"
                          aria-label="Clona"
                          onClick={() => void handleClone(p)}
                        />
                        {!p.isActive && (
                          <IconButton
                            icon="check_circle"
                            size="sm"
                            aria-label="Attiva"
                            onClick={() => void handleActivate(p)}
                          />
                        )}
                        {configOn && (
                          <ButtonLink
                            to={`/products/${p.id}`}
                            variant="text"
                            size="sm"
                            icon="tune"
                            aria-label="Configurazione"
                            title="Configurazione"
                          />
                        )}
                        <ButtonLink
                          to={`/products/${p.id}/governance`}
                          variant="text"
                          size="sm"
                          icon="account_tree"
                          aria-label="Dove e' usato"
                          title="Dove e' usato"
                        />
                      </>
                    )}
                    <IconButton
                      icon="delete"
                      size="sm"
                      aria-label="Elimina"
                      onClick={() => void handleDelete(p)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}

      {/* Create/Edit Product Dialog (TASK-0077) */}
      <Dialog
        open={showModal}
        onClose={closeModal}
        title={editId !== null ? 'Modifica prodotto' : 'Nuovo prodotto'}
        as="form"
        onSubmit={e => { e.preventDefault(); void handleSave() }}
        actions={
          <>
            <Button type="button" variant="text" onClick={closeModal}>
              Annulla
            </Button>
            <Button
              type="submit"
              variant="text"
              disabled={loadingDetail || saving}
            >
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        {loadingDetail ? (
          <LoadingState />
        ) : (
          <>
            {saveError && <Callout tone="error">{saveError}</Callout>}
            <FormSection title="Prodotto">
              <TextField
                label="Nome *"
                value={form.name}
                onChange={e => handleNameChange(e.target.value)}
              />
              <TextField
                label="Codice"
                value={form.code}
                onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
              />
              <TextField
                className="md-span-full"
                label="Slug *"
                value={form.slug}
                onChange={e => handleSlugChange(e.target.value)}
                supportingText="Generato automaticamente dal nome, modificabile."
              />
            </FormSection>
            <FormSection title="Classificazione">
              <Select
                label="Reparto"
                value={form.departmentId}
                onChange={e => {
                  const val = e.target.value
                  // Changing department invalidates a category/operator picked under
                  // the previous department — cleared so the form can't silently
                  // submit a mismatched combination.
                  setForm(f => ({
                    ...f,
                    departmentId: val ? Number(val) : '',
                    categoryId: '',
                    operatorId: '',
                  }))
                }}
              >
                <option value="">—</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </Select>
              <Select
                label="Categoria"
                value={form.categoryId}
                onChange={e => {
                  const val = e.target.value
                  setForm(f => ({ ...f, categoryId: val ? Number(val) : '' }))
                }}
              >
                <option value="">—</option>
                {categoryOptions.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
              <Select
                label="Operatore"
                value={form.operatorId}
                onChange={e => {
                  const val = e.target.value
                  setForm(f => ({ ...f, operatorId: val ? Number(val) : '' }))
                }}
              >
                <option value="">—</option>
                {operatorOptions.map(o => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </Select>
              {/* TASK-0156 (REQ-006-007): "—" (Tutti) submits as null, which the backend
                  reads as "applies to both person and company", not "unset" — see
                  Product.ApplicableCustomerType's own doc comment (backend). */}
              <Select
                label="Tipo cliente applicabile"
                value={form.applicableCustomerType}
                onChange={e => setForm(f => ({ ...f, applicableCustomerType: e.target.value }))}
              >
                <option value="">Tutti</option>
                <option value="person">Persona</option>
                <option value="company">Azienda</option>
              </Select>
            </FormSection>
            <FormSection title="Prezzi">
              <TextField
                label="Prezzo Base (€) *"
                type="number"
                min={0}
                step={0.01}
                value={form.basePrice}
                onChange={e =>
                  setForm(f => ({ ...f, basePrice: e.target.value ? parseFloat(e.target.value) : '' }))
                }
              />
              <TextField
                label="Commissione (€) *"
                type="number"
                min={0}
                step={0.01}
                value={form.commissionValue}
                onChange={e =>
                  setForm(f => ({ ...f, commissionValue: e.target.value ? parseFloat(e.target.value) : '' }))
                }
              />
              {/* REQ-018-002: "Ricavo azienda" — the operator price list figure for this
                  operator/product pair. Optional (nullable column), so no `*` marker and no
                  entry in handleSave's required-fields guard; `min={0}` mirrors the
                  server-side EnsureNonNegative floor, same as Prezzo Base/Commissione. */}
              <TextField
                label="Ricavo azienda (€)"
                type="number"
                min={0}
                step={0.01}
                value={form.companyRevenue}
                onChange={e =>
                  setForm(f => ({ ...f, companyRevenue: e.target.value ? parseFloat(e.target.value) : '' }))
                }
                supportingText="Ricavo dell'azienda dall'operatore per questo prodotto (listino operatore)."
              />
              <TextField
                label="Tipo Prezzo"
                placeholder="es. commission_only"
                value={form.pricingType}
                onChange={e => setForm(f => ({ ...f, pricingType: e.target.value }))}
              />
            </FormSection>
            <FormSection title="Validità">
              {/* TASK-0164 (REQ-043-002): "Periodo di validità" — both independently
                  optional, "" submits as null (no restriction on that end). Product.cs's
                  EnsureValidDateRange guard rejects validTo before validFrom server-side;
                  no client-side pre-check is added here (the 400 already surfaces via
                  saveError above). */}
              <TextField
                label="Valido dal"
                type="date"
                value={form.validFrom}
                onChange={e => setForm(f => ({ ...f, validFrom: e.target.value }))}
              />
              <TextField
                label="Valido al"
                type="date"
                value={form.validTo}
                onChange={e => setForm(f => ({ ...f, validTo: e.target.value }))}
              />
            </FormSection>
            <FormSection title="Descrizione" columns={1}>
              <TextField
                label="Descrizione"
                multiline
                rows={2}
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              />
            </FormSection>
            <FormSection title="Opzioni" columns={1}>
              <Checkbox
                label="Richiede approvazione"
                checked={form.requiresApproval}
                onChange={e => setForm(f => ({ ...f, requiresApproval: e.target.checked }))}
              />
              <Checkbox
                label="Visibile agli agenti"
                checked={form.visibleToAgents}
                onChange={e => setForm(f => ({ ...f, visibleToAgents: e.target.checked }))}
              />
              <Checkbox
                label="Opzione (si aggiunge a una pratica)"
                checked={form.isOption}
                onChange={e => setForm(f => ({ ...f, isOption: e.target.checked }))}
              />
              <Select
                label="Mandante (vuota = quella dell'operatore)"
                value={form.mandanteId}
                onChange={e => {
                  const val = e.target.value
                  setForm(f => ({ ...f, mandanteId: val ? Number(val) : '' }))
                }}
              >
                <option value="">—</option>
                {mandanti.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </Select>
            </FormSection>
          </>
        )}
      </Dialog>

      {/* Coraly A: mandante per operatore (vale per i prodotti senza mandante propria). */}
      <Dialog
        open={showOperators}
        onClose={() => setShowOperators(false)}
        title="Mandanti degli operatori"
        actions={<Button type="button" variant="text" onClick={() => setShowOperators(false)}>Chiudi</Button>}
      >
        <FormSection title="Mandante per operatore" columns={1}>
          {operators.map(o => (
            <Select
              key={o.id}
              label={o.name}
              value={o.mandanteId ?? ''}
              onChange={e => {
                const val = e.target.value
                const next = val ? Number(val) : null
                void updateOperatorMandante(o.id, next, 'Mandante operatore da catalogo')
                  .then(() => setOperators(list => list.map(x => x.id === o.id ? { ...x, mandanteId: next } : x)))
                  .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Errore nel salvataggio'))
              }}
            >
              <option value="">—</option>
              {mandanti.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </Select>
          ))}
        </FormSection>
      </Dialog>
    </Page>
  )
}
